// SEPA-Mandate + Lastschrift-Hooks.

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface SepaMandate {
  id: string;
  customer_id: string;
  mandate_ref: string;
  iban_last4: string;
  bic: string | null;
  account_holder: string;
  signed_at: string;
  pdf_url: string | null;
  revoked_at: string | null;
  is_active: boolean;
  created_at: string;
}

export interface SepaBatch {
  id: string;
  batch_ref: string;
  status: "draft" | "generated" | "submitted" | "cleared" | "partial_returned";
  xml_url: string | null;
  total_cents: number;
  collection_date: string;
  submitted_at: string | null;
  created_at: string;
}

// ---------- Mandate ----------

export function useMyMandate() {
  return useQuery({
    queryKey: ["my-mandate"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sepa_mandates_public")
        .select("*")
        .is("revoked_at", null)
        .order("signed_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data as SepaMandate | null;
    },
  });
}

export function useAllMandates() {
  return useQuery({
    queryKey: ["all-mandates"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sepa_mandates")
        .select("*, customer:customers(display_name, email)")
        .order("signed_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as Array<SepaMandate & { customer: { display_name: string; email: string | null } | null }>;
    },
  });
}

export function useCreateMandate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ customerId, iban, bic, accountHolder }: {
      customerId: string; iban: string; bic?: string; accountHolder: string;
    }) => {
      const cleanIban = iban.replace(/\s+/g, "").toUpperCase();
      if (cleanIban.length < 15 || cleanIban.length > 34) throw new Error("IBAN ungültig");
      const last4 = cleanIban.slice(-4);
      // Hash via SubtleCrypto — irreversibel
      const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(cleanIban));
      const hash = Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("");

      const yr = new Date().getFullYear();
      const { data: ref, error: refErr } = await supabase.rpc("next_mandate_ref", { year_in: yr });
      if (refErr) throw refErr;

      const { error } = await supabase.from("sepa_mandates").insert({
        customer_id: customerId,
        mandate_ref: ref,
        iban_last4: last4,
        iban_hash: hash,
        iban_full: cleanIban,
        bic: bic ?? null,
        account_holder: accountHolder,
        signed_at: new Date().toISOString(),
      });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-mandate"] });
      qc.invalidateQueries({ queryKey: ["all-mandates"] });
    },
  });
}

export function useRevokeMandate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from("sepa_mandates")
        .update({ revoked_at: new Date().toISOString() })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["my-mandate"] });
      qc.invalidateQueries({ queryKey: ["all-mandates"] });
    },
  });
}

// ---------- SEPA-Läufe ----------

export function useSepaBatches() {
  return useQuery({
    queryKey: ["sepa-batches"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sepa_batches")
        .select("*")
        .order("created_at", { ascending: false });
      if (error) throw error;
      return (data ?? []) as SepaBatch[];
    },
  });
}
